<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { t } from '../i18n';
import { tell } from '../dialogs';
import AppDialog from './AppDialog.vue';
import { createProfile, displayName, listProfiles, MAX_PROFILES, ProfileLimitError, type ProfileRecord } from '../profiles/registry';
import { hashPin, PIN_RE } from '../profiles/pin';
import { pickPicture, pictureFromFile, PictureError } from '../profiles/picture';
import { enterProfile, type GateMode } from '../profiles/session';
import { importProfileFile } from '../profiles/transfer';
import { folderSupported, FolderChoiceError, pickFolderForProfile } from '../storage-location';
import type { ProfileStorage } from '../profiles/registry';
import PinForm from './PinForm.vue';
import ProfileAvatar from './ProfileAvatar.vue';
import ProfileDeleteForm from './ProfileDeleteForm.vue';
import Icon from './Icon.vue';

/**
 * Before the app opens (docs/TZ-PROFILES.md 5): creating the first profile, picking one, entering
 * its PIN, or deleting a profile whose PIN is forgotten. Ends when a profile is open.
 */
const props = defineProps<{ mode: GateMode }>();
const emit = defineEmits<{ done: [] }>();

type Screen = { kind: 'create' } | { kind: 'choose' } | { kind: 'unlock'; profile: ProfileRecord } | { kind: 'delete'; profile: ProfileRecord };
const screen = ref<Screen>(props.mode);
const profiles = ref<ProfileRecord[]>([]);
const message = ref('');

// creating
const firstName = ref('');
const lastName = ref('');
const avatar = ref('');
const withPin = ref(false);
const pin = ref('');
const pin2 = ref('');
const busy = ref(false);
const nameField = ref<HTMLInputElement | null>(null);

// where the canvases will be: chosen now, it cannot be changed later
const place = ref<'browser' | 'folder'>('browser');
const folder = ref<{ handle: FileSystemDirectoryHandle; name: string; mode: 'own' | 'shared' | 'ask' } | null>(null);
const folderMode = ref<'own' | 'shared' | ''>('');
const folderError = ref('');

const pinOk = computed(() => !withPin.value || (PIN_RE.test(pin.value) && pin.value === pin2.value));
const placeOk = computed(() => place.value === 'browser' || (!!folder.value && (folder.value.mode !== 'ask' || !!folderMode.value)));
const canCreate = computed(() => !!firstName.value.trim() && pinOk.value && placeOk.value && !busy.value);

async function chooseFolder(): Promise<void> {
  folderError.value = '';
  try {
    const picked = await pickFolderForProfile();
    if (!picked) return;
    folder.value = picked;
    folderMode.value = picked.mode === 'ask' ? '' : picked.mode;
  } catch (e) {
    folderError.value = e instanceof FolderChoiceError ? t('profiles.folderOwned') : t('profiles.folderFailed', { msg: e instanceof Error ? e.message : String(e) });
  }
}
function storageChoice(): ProfileStorage {
  if (place.value === 'browser' || !folder.value) return { kind: 'browser' };
  return { kind: 'folder', mode: folder.value.mode === 'ask' ? (folderMode.value as 'own' | 'shared') : folder.value.mode, handle: folder.value.handle, name: folder.value.name };
}
const full = computed(() => profiles.value.length >= MAX_PROFILES);

onMounted(async () => {
  profiles.value = await listProfiles();
  if (screen.value.kind === 'create') void nextTick(() => nameField.value?.focus());
});

function show(s: Screen): void {
  message.value = '';
  screen.value = s;
  if (s.kind === 'create') {
    firstName.value = lastName.value = avatar.value = pin.value = pin2.value = '';
    withPin.value = false;
    place.value = 'browser';
    folder.value = null;
    folderMode.value = folderError.value = '';
    void nextTick(() => nameField.value?.focus());
  }
}

async function choosePicture(): Promise<void> {
  const file = await pickPicture();
  if (!file) return;
  try {
    avatar.value = await pictureFromFile(file);
  } catch (e) {
    message.value = e instanceof PictureError && e.message === 'size' ? t('profiles.pictureTooBig') : t('profiles.pictureBad');
  }
}

async function open(p: ProfileRecord): Promise<void> {
  await enterProfile(p);
  emit('done');
}

async function create(): Promise<void> {
  if (!canCreate.value || busy.value) return;
  await tell(place.value === 'browser' ? t('profiles.warnBrowser') : t('profiles.warnFolder'), { title: t('profiles.warnTitle') });
  if (!canCreate.value) return;
  busy.value = true;
  message.value = '';
  try {
    const p = await createProfile({ firstName: firstName.value, lastName: lastName.value, avatar: avatar.value, pin: withPin.value ? await hashPin(pin.value) : null, storage: storageChoice() });
    await open(p);
  } catch (e) {
    message.value = e instanceof ProfileLimitError ? t('profiles.limit', { n: MAX_PROFILES }) : t('profiles.createFailed', { msg: e instanceof Error ? e.message : String(e) });
  } finally {
    busy.value = false;
  }
}

function pick(p: ProfileRecord): void {
  if (p.pin) show({ kind: 'unlock', profile: p });
  else void open(p);
}

async function importFile(): Promise<void> {
  message.value = '';
  try {
    const p = await importProfileFile();
    if (p) await open(p);
  } catch (e) {
    message.value = e instanceof ProfileLimitError ? t('profiles.limit', { n: MAX_PROFILES }) : t('profiles.importFailed', { msg: e instanceof Error ? e.message : String(e) });
  }
}

async function deleted(folderOk: boolean): Promise<void> {
  profiles.value = await listProfiles();
  show(profiles.value.length ? { kind: 'choose' } : { kind: 'create' });
  message.value = folderOk ? t('profiles.deleted') : t('profiles.deletedFolderLeft');
}
</script>

<template>
  <div class="gate">
    <div class="float card" role="dialog" aria-modal="true" :aria-label="t('profiles.title')" data-dialog="profile-gate">
      <!-- creating a profile -->
      <form v-if="screen.kind === 'create'" class="create" data-screen="create" @submit.prevent="create">
        <h2>{{ profiles.length ? t('profiles.newTitle') : t('profiles.firstTitle') }}</h2>
        <p v-if="!profiles.length" class="muted lead">{{ t('profiles.firstLead') }}</p>
        <div class="pic">
          <ProfileAvatar id="new" :first-name="firstName || '?'" :last-name="lastName" :avatar="avatar" :size="84" />
          <div class="pic-actions">
            <button type="button" class="pill" data-act="choose-picture" @click="choosePicture">{{ t('profile.avatarChoose') }}</button>
            <button v-if="avatar" type="button" class="pill" @click="avatar = ''">{{ t('profiles.pictureRemove') }}</button>
          </div>
        </div>
        <label class="field">
          <span>{{ t('profile.firstName') }} <em>*</em></span>
          <input ref="nameField" v-model="firstName" class="input" maxlength="80" autocomplete="given-name" data-field="first-name" />
        </label>
        <label class="field">
          <span>{{ t('profile.lastName') }}</span>
          <input v-model="lastName" class="input" maxlength="80" autocomplete="family-name" data-field="last-name" />
        </label>
        <div class="field">
          <span>{{ t('profiles.placeTitle') }}</span>
          <div class="seg-pills place" role="radiogroup" :aria-label="t('profiles.placeTitle')">
            <button type="button" role="radio" :aria-checked="place === 'browser'" :class="{ active: place === 'browser' }" data-place-new="browser" @click="place = 'browser'">{{ t('storage.place.browser') }}</button>
            <button type="button" role="radio" :aria-checked="place === 'folder'" :class="{ active: place === 'folder' }" :disabled="!folderSupported" data-place-new="folder" @click="place = 'folder'">{{ t('storage.place.folder') }}</button>
          </div>
          <template v-if="place === 'folder'">
            <div class="folder-row">
              <button type="button" class="pill" data-act="pick-folder" @click="chooseFolder">{{ folder ? t('profiles.folderOther') : t('profiles.folderPick') }}</button>
              <span v-if="folder" class="folder-name" data-field="new-folder">{{ folder.name }}</span>
            </div>
            <div v-if="folder?.mode === 'ask'" class="seg-pills place" role="radiogroup" :aria-label="t('profiles.folderKindTitle')">
              <button type="button" role="radio" :aria-checked="folderMode === 'own'" :class="{ active: folderMode === 'own' }" data-folder-mode="own" @click="folderMode = 'own'">{{ t('profiles.folderOwn') }}</button>
              <button type="button" role="radio" :aria-checked="folderMode === 'shared'" :class="{ active: folderMode === 'shared' }" data-folder-mode="shared" @click="folderMode = 'shared'">{{ t('profiles.folderShared') }}</button>
            </div>
            <p v-if="folder?.mode === 'shared'" class="note">{{ t('profiles.folderIsShared') }}</p>
            <p v-if="folderError" class="note warn" role="alert">{{ folderError }}</p>
          </template>
          <p v-if="!folderSupported" class="note">{{ t('profiles.placeNoFolder') }}</p>
        </div>
        <label class="check">
          <input v-model="withPin" type="checkbox" data-field="with-pin" />
          <span>{{ t('profiles.protect') }}</span>
        </label>
        <template v-if="withPin">
          <div class="pins">
            <input v-model="pin" class="input" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" :placeholder="t('profiles.pinNew')" :aria-label="t('profiles.pinNew')" data-field="pin-new" @input="pin = pin.replace(/\D/g, '')" />
            <input v-model="pin2" class="input" type="password" inputmode="numeric" maxlength="8" autocomplete="new-password" :placeholder="t('profiles.pinRepeat')" :aria-label="t('profiles.pinRepeat')" data-field="pin-repeat" @input="pin2 = pin2.replace(/\D/g, '')" />
          </div>
          <p class="note">{{ t('profiles.pinNote') }}</p>
          <p v-if="pin2 && pin !== pin2" class="note warn">{{ t('profiles.pinMismatch') }}</p>
        </template>
        <p v-if="message" class="note warn" role="alert">{{ message }}</p>
        <button type="submit" class="pill primary tall wide" :disabled="!canCreate" data-act="create-profile">{{ t('profiles.create') }}</button>
        <div class="links">
          <button v-if="profiles.length" type="button" class="pill" @click="show({ kind: 'choose' })">{{ t('common.cancel') }}</button>
          <button type="button" class="pill" data-act="import-profile" @click="importFile">{{ t('profiles.import') }}</button>
        </div>
      </form>

      <!-- picking a profile -->
      <div v-else-if="screen.kind === 'choose'" class="choose" data-screen="choose">
        <h2>{{ t('profiles.chooseTitle') }}</h2>
        <ul class="tiles">
          <li v-for="p in profiles" :key="p.id">
            <button type="button" class="tile" :data-profile="p.firstName" @click="pick(p)">
              <ProfileAvatar :id="p.id" :first-name="p.firstName" :last-name="p.lastName" :avatar="p.avatar" :size="64" />
              <span class="name">{{ displayName(p) }}</span>
              <span v-if="p.pin" class="lock" :title="t('profiles.locked')"><Icon name="lock" :size="13" /></span>
            </button>
          </li>
          <li v-if="!full">
            <button type="button" class="tile add" data-act="new-profile" @click="show({ kind: 'create' })">
              <span class="plus">+</span>
              <span class="name">{{ t('profiles.new') }}</span>
            </button>
          </li>
        </ul>
        <p v-if="full" class="note">{{ t('profiles.limit', { n: MAX_PROFILES }) }}</p>
        <p v-if="message" class="note warn" role="alert">{{ message }}</p>
        <div class="links">
          <button type="button" class="pill" data-act="import-profile" @click="importFile">{{ t('profiles.import') }}</button>
        </div>
      </div>

      <!-- the PIN -->
      <div v-else-if="screen.kind === 'unlock'" class="unlock" data-screen="unlock">
        <PinForm :profile="screen.profile" @ok="open((screen as { profile: ProfileRecord }).profile)">
          <div class="links stack">
            <button type="button" class="pill" data-act="other-profile" @click="show({ kind: 'choose' })">{{ t('profiles.other') }}</button>
            <button type="button" class="pill" data-act="forgot-pin" @click="show({ kind: 'delete', profile: (screen as { profile: ProfileRecord }).profile })">{{ t('profiles.forgot') }}</button>
          </div>
        </PinForm>
      </div>

      <!-- deleting (the PIN is forgotten) -->
      <ProfileDeleteForm v-else :profile="screen.profile" @done="deleted" @cancel="show({ kind: 'unlock', profile: (screen as { profile: ProfileRecord }).profile })" />
    </div>
  </div>
  <AppDialog />
</template>

<style scoped>
.gate {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: 16px;
  overflow: auto;
  background: var(--bg);
}
.card {
  width: min(460px, 100%);
  padding: 26px 26px 22px;
  border-radius: var(--radius-panel);
}
h2 {
  margin: 0;
  font-size: 20px;
  text-align: center;
}
.lead {
  margin: -4px 0 0;
  text-align: center;
  line-height: 1.5;
}
.create,
.choose {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.pic {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.pic-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.field {
  display: grid;
  gap: 6px;
  font-size: 13px;
}
.field em {
  color: var(--danger);
  font-style: normal;
}
.input {
  height: 40px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--bg);
  color: var(--text);
  font: inherit;
  font-size: 14px;
}
.input:focus {
  outline: none;
  border-color: var(--accent);
}
.place {
  justify-self: center;
  width: max-content;
}
.folder-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 0;
}
.folder-name {
  font-family: ui-monospace, Consolas, monospace;
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.check {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  cursor: pointer;
}
.check input {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  margin: 0;
  flex: none;
  border: 1.5px solid var(--border-strong);
  border-radius: 6px;
  background: var(--panel);
  display: grid;
  place-items: center;
  cursor: pointer;
}
.check input:checked {
  background: var(--accent);
  border-color: var(--accent);
}
.check input:checked::after {
  content: '';
  display: block;
  width: 4px;
  height: 8px;
  margin-top: -2px;
  border: solid var(--on-accent);
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.check input:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.pins {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.note {
  margin: -4px 0 0;
  font-size: 12px;
  color: var(--text-2);
  line-height: 1.45;
}
.warn {
  color: var(--warn);
}
.wide {
  justify-content: center;
}
.links {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
}
.links.stack {
  flex-direction: column;
  align-items: center;
}
.links.stack .pill {
  width: 200px;
  justify-content: center;
}
.link {
  border: none;
  background: none;
  padding: 0;
  color: var(--accent-ink, var(--accent));
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  align-self: center;
}
.tiles {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: 10px;
}
.tile {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 8px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  background: var(--panel);
  color: var(--text);
  font: inherit;
  cursor: pointer;
}
.tile:hover {
  border-color: var(--accent);
}
.tile .name {
  font-size: 13px;
  font-weight: 550;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lock {
  position: absolute;
  top: 9px;
  right: 10px;
  color: var(--text-2);
}
.add .plus {
  width: 64px;
  height: 64px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: 2px dashed var(--border-strong, var(--border));
  font-size: 28px;
  color: var(--text-2);
}
.unlock {
  display: flex;
  flex-direction: column;
}
</style>
